import HomeIcon from "@mui/icons-material/Home";
import ApartmentIcon from "@mui/icons-material/Apartment";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import DomainIcon from "@mui/icons-material/Domain";
import BusinessIcon from "@mui/icons-material/Business";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import InfoIcon from "@mui/icons-material/Info";
import MapIcon from "@mui/icons-material/Map";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import TopicIcon from "@mui/icons-material/Topic";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import EmailIcon from "@mui/icons-material/Email";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import RecyclingIcon from "@mui/icons-material/Recycling";
import { ORDER_URL, MANUAL_URL } from "@/configs/constants";
import {
  REASSIST_LABELS,
  REASSIST_STATUS_PATH,
} from "@/features/reassistRequests/config";
import { ReassistMarkIcon } from "@/features/reassistRequests/components/ReassistMarkIcon";
import { ReassistSideMenuSection } from "@/features/reassistRequests/components/ReassistSideMenuSection";
import {
  REREPO_LABELS,
  REREPO_REQUEST_PATH,
} from "@/features/realEstateResearchRequest/config";
import { RerepoMarkIcon } from "@/features/realEstateResearchRequest/components/RerepoMarkIcon";
import { RerepoSideMenuSection } from "@/features/realEstateResearchRequest/components/RerepoSideMenuSection";
import {
  INHERITANCE_REUSE_PATH,
  INHERITANCE_REUSE_TITLE,
} from "@/features/inheritanceReuse/utils/constants";

/**
 * メニュー項目の型定義
 */
export interface MenuItemData {
  label: string;
  href?: string;
  disabled?: boolean;
  icon?: React.ElementType;
  // 月額会員限定を示す上品なプレミアムマーカー（末尾に小さなゴールド王冠を表示）。
  premium?: boolean;
  // true の項目はメニューから完全に非表示（disabled のグレー表示と異なり描画自体しない）。
  hidden?: boolean;
}

export interface SubCategoryData {
  title: string;
  items: MenuItemData[];
}

export interface CategoryData {
  title: string;
  icon: React.ElementType;
  href?: string;
  subCategories?: SubCategoryData[];
  items?: MenuItemData[];
  isPrimary?: boolean;
  actionType?: "contact-dialog";
  /** サービスの入口（R.E.ASSIST・R.E.REPO）として出す場合に指定する。 */
  serviceSection?: ServiceSectionData;
}

/**
 * サイドバーに置くサービスの入口。展開時は Section（ロゴとメニュー行）を出す。
 * 折りたたみ時は href へのマーク（icon）だけを出し、サービス名を aria-label と Tooltip で伝える。
 */
export interface ServiceSectionData {
  /** サービス名（例: R.E.ASSIST）。 */
  name: string;
  /** 展開時に出す入口。onNavigate はリンクを押したときの処理（スマホのメニューを閉じるのに使う）。 */
  Section: React.FC<{ onNavigate?: () => void }>;
}

/**
 * フィーチャーフラグの型定義
 */
export interface SideMenuFeatureFlags {
  realEstateRegistrationAggregationOfNames: boolean;
  realEstateRegistrationMonitoring: boolean;
  commercialBookList: boolean;
  corporateMaster: boolean;
  commercialCorporationList?: boolean;
  commercialRegistrationReceptionBook: boolean;
  commercialRegistrationAggregationOfNames: boolean;
  commercialRegistrationMonitoring: boolean;
  commercialPictureAnalysis: boolean;
  allMattersAnalysis: boolean;
  dmManagement: boolean;
  emailManagement: boolean;
  customerManagement: boolean;
  ownerInfoList: boolean;
  realEstateTitle: boolean;
  tradePriceSearch: boolean;
  rightsSectionOtsukuSearch: boolean;
  inheritanceReuseCandidates: boolean;
  realEstateResearchRequest: boolean;
}

/**
 * メインカテゴリのメニューデータを生成
 */
export const getCategories = (flags: SideMenuFeatureFlags): CategoryData[] => [
  {
    title: "ホーム",
    icon: HomeIcon,
    href: "/mypage",
    isPrimary: true,
  },
  {
    title: "不動産登記",
    icon: ApartmentIcon,
    isPrimary: true,
    subCategories: [
      {
        // 月額会員限定の特別項目。「取得する・探す」サブ見出しより前（グループ直前）に
        // 単独配置して上部に目立たせる。title 空でサブ見出しは描画されない。
        // 非会員（またはフラグ未付与）には項目・グループごと非表示にする。
        title: "",
        items: [
          {
            label: INHERITANCE_REUSE_TITLE,
            href: INHERITANCE_REUSE_PATH,
            hidden: !flags.inheritanceReuseCandidates,
            icon: RecyclingIcon,
            premium: true,
          },
        ],
      },
      {
        title: "取得する・探す",
        items: [
          { label: "地図・住所から", href: "/map", icon: MapIcon },
          {
            label: "地番・家屋番号から",
            href: "/acquirebook",
            icon: RequestPageIcon,
          },
          {
            label: "地番・家屋番号リストから",
            href: "/acquiremultiplebooks/upload",
            icon: RequestQuoteIcon,
          },
          {
            label: "取得済不動産登記から",
            href: "/acquirebooklist",
            icon: TopicIcon,
          },
          {
            label: "不動産オーナーから",
            href: "/storedpictures",
            disabled: !flags.realEstateRegistrationAggregationOfNames,
            icon: FindInPageIcon,
          },
          {
            label: "権利部（乙区）情報から",
            href: "/rightssectionotsuku",
            disabled: !flags.rightsSectionOtsukuSearch,
            icon: FindInPageIcon,
          },
          {
            label: "不動産の権利異動から",
            href: "/feed",
            icon: MenuBookIcon,
          },
          {
            label: "物件種別から",
            href: "/titlesearch",
            disabled: !flags.realEstateTitle,
            icon: LocationCityIcon,
          },
          {
            label: "不動産登記モニタリング",
            href: "/monitoring",
            disabled: !flags.realEstateRegistrationMonitoring,
            icon: MonitorHeartIcon,
          },
          {
            label: "オペレーターに依頼",
            href: ORDER_URL,
            icon: TrendingUpIcon,
          },
        ],
      },
      {
        title: "データ化する",
        items: [
          {
            label: "所有者事項データ化",
            href: "/ownerinfo/upload",
            disabled: !flags.ownerInfoList,
            icon: PictureAsPdfIcon,
          },
          {
            label: "所有者事項データ化結果一覧",
            href: "/ownerinfo/list",
            disabled: !flags.ownerInfoList,
            icon: LibraryBooksIcon,
          },
          {
            label: "全部事項データ化",
            href: "/allmatters/upload",
            disabled: !flags.allMattersAnalysis,
            icon: PictureAsPdfIcon,
          },
          {
            label: "全部事項データ化結果一覧",
            href: "/allmatters/list",
            disabled: !flags.allMattersAnalysis,
            icon: LibraryBooksIcon,
          },
        ],
      },
    ],
  },
  {
    title: "商業登記",
    icon: CorporateFareIcon,
    isPrimary: true,
    subCategories: [
      {
        title: "",
        items: [
          {
            label: "法人情報から",
            href: "/corporate-master",
            disabled: !flags.corporateMaster,
            icon: DomainIcon,
          },
          {
            label: "法人名から",
            href: "/commercialbook",
            disabled: !flags.commercialBookList,
            // 法人情報の画面から移れるため、法人情報を使えるユーザーには出さない
            hidden: flags.corporateMaster,
            icon: RequestPageIcon,
          },
          {
            label: "法人リストから",
            href: "/commercialbook/corporationlist",
            hidden: !flags.commercialCorporationList,
            disabled: !flags.commercialBookList,
            icon: RequestPageIcon,
          },
          {
            label: "取得済商業登記から",
            href: "/commercialbook/list",
            disabled: !flags.commercialBookList,
            icon: TopicIcon,
          },
          {
            label: "取締役名から",
            disabled: !flags.commercialRegistrationAggregationOfNames,
            icon: FindInPageIcon,
          },
          {
            label: "法人の権利異動から",
            href: "/commercialregistrationreceptionbook",
            disabled: !flags.commercialRegistrationReceptionBook,
            icon: MenuBookIcon,
          },
          {
            label: "商業登記モニタリング",
            href: "/commercial-monitoring",
            disabled: !flags.commercialRegistrationMonitoring,
            icon: MonitorHeartIcon,
          },
          {
            label: "オペレーターに依頼",
            disabled: true,
            icon: TrendingUpIcon,
          },
        ],
      },
      {
        title: "データ化する",
        items: [
          {
            label: "商業登記データ化",
            href: "/commercialpictureanalysis/upload",
            disabled: !flags.commercialPictureAnalysis,
            icon: PictureAsPdfIcon,
          },
          {
            label: "商業登記データ化結果一覧",
            href: "/commercialpictureanalysis/list",
            disabled: !flags.commercialPictureAnalysis,
            icon: LibraryBooksIcon,
          },
        ],
      },
    ],
  },
  {
    title: "顧客管理",
    icon: BusinessIcon,
    isPrimary: true,
    subCategories: [
      {
        title: "",
        items: [
          {
            label: "顧客管理",
            href: "/customermanagement/customerList",
            disabled: !flags.customerManagement,
            icon: ManageSearchIcon,
          },
          {
            label: "メール管理",
            href: "/emailManagement/mail/savedEmailList",
            disabled: !flags.emailManagement,
            icon: EmailIcon,
          },
          {
            label: "DM管理",
            href: "/dmManagement/orders",
            disabled: !flags.dmManagement,
            icon: ContactMailIcon,
          },
        ],
      },
    ],
  },
  // 送信先がつながるまではフラグで入口ごと出さない（グレー表示にもしない）
  ...(flags.realEstateResearchRequest
    ? [
        {
          title: "不動産調査依頼",
          // 折りたたみ時はマークだけの版を出す（文字入りのロゴを縮めて押し込まない）
          icon: RerepoMarkIcon,
          href: REREPO_REQUEST_PATH,
          isPrimary: true,
          serviceSection: {
            name: REREPO_LABELS.service,
            Section: RerepoSideMenuSection,
          },
        },
      ]
    : []),
  {
    title: "役所資料取得依頼",
    // 折りたたみ時はマークだけの版を出す（文字入りのロゴを縮めて押し込まない）
    icon: ReassistMarkIcon,
    // 依頼状況・請求の画面。依頼する・資料を探すボタンもここにある
    href: REASSIST_STATUS_PATH,
    isPrimary: true,
    serviceSection: {
      name: REASSIST_LABELS.service,
      Section: ReassistSideMenuSection,
    },
  },
];

/**
 * サポートリンクのメニューデータ
 */
export const supportLinks: CategoryData[] = [
  { title: "マニュアル", href: MANUAL_URL, icon: AutoStoriesIcon },
  { title: "お問い合わせ", actionType: "contact-dialog", icon: InfoIcon },
];
